import React, { useEffect, useState } from 'react';
import { bool, object } from 'prop-types';
import { connect } from 'react-redux';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import SeoIndexAlphabetNav from '../../components/SeoDiscovery/SeoIndexAlphabetNav';
import { propTypes } from '../../util/types';
import css from '../../components/SeoDiscovery/SeoDiscovery.module.css';
import { fetchSeoVenuesIndex } from '../../util/seoDiscoveryFetch';
import { INDEX_LETTERS, venuesLetterPath } from '../../util/seoDiscoveryIndexLetters';

export const SeoDiscoveryVenuesIndexPageComponent = props => {
  const { data, fetchInProgress, fetchError } = props;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const letterCounts = data?.letterCounts || {};
  const normalizedQuery = searchQuery.trim();
  const hasSearch = normalizedQuery.length > 0;
  const venues = hasSearch ? searchResults?.venues || [] : [];
  const total = hasSearch ? searchResults?.total || 0 : 0;

  useEffect(() => {
    if (!hasSearch) {
      setSearchResults(null);
      return undefined;
    }
    setSearchInProgress(true);
    const handle = window.setTimeout(() => {
      fetchSeoVenuesIndex({
        launched: 'true',
        sort: 'name',
        q: normalizedQuery,
        limit: '500',
      })
        .then(payload => setSearchResults(payload))
        .catch(() => setSearchResults({ venues: [], total: 0 }))
        .finally(() => setSearchInProgress(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [hasSearch, normalizedQuery]);

  return (
    <Page
      title="Venues | Event Discovery | PromoTix"
      description="Browse venues on PromoTix. Find tickets, seating guides, parking, hotels, and upcoming events."
      schema={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'PromoTix Venues',
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <h1 className={css.h1}>Venues</h1>
          <p>Explore venue guides with upcoming events, seating, parking, and local tips.</p>
          {fetchError ? <p>Unable to load venues.</p> : null}
          {fetchInProgress && !Object.keys(letterCounts).length ? <p>Loading…</p> : null}
          <div className={css.venueSearch}>
            <label className={css.venueSearchLabel} htmlFor="venue-index-search">
              Find a specific venue
            </label>
            <div className={css.venueSearchControl}>
              <input
                id="venue-index-search"
                className={css.venueSearchInput}
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by venue, city, or state"
                autoComplete="off"
              />
              {hasSearch ? (
                <button
                  className={css.venueSearchClear}
                  type="button"
                  onClick={() => setSearchQuery('')}
                >
                  Clear
                </button>
              ) : null}
            </div>
            {hasSearch ? (
              <p className={css.venueSearchHelp} aria-live="polite">
                {searchInProgress
                  ? 'Searching…'
                  : `${total} venue${total === 1 ? '' : 's'} found`}
              </p>
            ) : null}
          </div>
          {!hasSearch ? (
            <>
              <SeoIndexAlphabetNav letterCounts={letterCounts} getLetterHref={venuesLetterPath} />
              <ul className={css.indexHubLetters}>
                {INDEX_LETTERS.filter(letter => (letterCounts[letter] || 0) > 0).map(letter => (
                  <li key={letter}>
                    <a href={venuesLetterPath(letter)}>
                      {letter === '#' ? '0–9' : letter} — {letterCounts[letter]} venue
                      {letterCounts[letter] === 1 ? '' : 's'}
                    </a>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {hasSearch && venues.length > 0 ? (
            <section className={css.letterSection}>
              <h2 className={css.letterHeading}>Search results</h2>
              <ul className={css.indexList}>
                {venues.map(v => (
                  <li key={v.venueId}>
                    <a href={v.path}>{v.venueName}</a>
                    {v.city ? (
                      <span className={css.eventMeta}>
                        {' '}
                        — {v.city}
                        {v.state ? `, ${v.state}` : ''}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {hasSearch && !searchInProgress && total === 0 ? (
            <p className={css.venueSearchNoResults}>
              No venues found for "{normalizedQuery}". Try a venue name, city, or state.
            </p>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoDiscoveryVenuesIndexPageComponent.propTypes = {
  data: object,
  fetchInProgress: bool,
  fetchError: propTypes.error,
};

const mapStateToProps = state => state.SeoDiscoveryVenuesIndexPage || {};

export default connect(mapStateToProps)(SeoDiscoveryVenuesIndexPageComponent);
